import React, { useMemo } from "react";
import type { WorkflowEnumItem } from "../hooks/useWorkflowEnums";
import type { ReactNode } from "react";

export interface StepTypePickerProps {
    items: WorkflowEnumItem[];
    onSelect: (code: string, label: string, meta?: unknown) => void;
    getIcon: (code: string) => ReactNode;
}

export const StepTypePicker: React.FC<StepTypePickerProps> = ({
    items,
    onSelect,
    getIcon
}) => {
    const groupedItems = useMemo(() => {
        const byGroup = new Map<string, WorkflowEnumItem[]>();
        for (const item of items ?? []) {
            const groupLabel = item.meta?.group ?? "Other";
            const groupItems = byGroup.get(groupLabel);
            if (groupItems) {
                groupItems.push(item);
            } else {
                byGroup.set(groupLabel, [item]);
            }
        }
        for (const groupItems of byGroup.values()) {
            groupItems.sort((a, b) => a.order - b.order);
        }
        return Array.from(byGroup.entries()).map(([groupLabel, groupItems]) => ({
            groupLabel,
            items: groupItems,
        }));
    }, [items]);

    return (
        <div className={`action-picker d-flex flex-column gap-3`}>
            {groupedItems.map(({ groupLabel, items: groupItems }) => (
                <div key={groupLabel} className="action-picker-group d-flex flex-column">
                    <div className="hs-fs-12 hs-color-light-grey-2">{groupLabel}</div>
                    {groupItems.map((item) => (
                        <div
                            key={item.code}
                            className="action-picker-card d-flex align-items-center gap-3 cursor-pointer p-2 rounded-2"
                            onClick={() => onSelect(item.code, item.idleLabel, item.meta)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    onSelect(item.code, item.idleLabel, item.meta);
                                }
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-center">
                                {getIcon(item.code)}
                            </div>
                            <div className="action-picker-text d-flex flex-column gap-1">
                                <span className="action-picker-title">{item.idleLabel}</span>
                                <span className="action-picker-desc">
                                    {item.meta?.description ?? item.idleLabel}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};
